import {FC, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {api, ApiError, BedWash, DoorCode, CleanerPayout, CleanerTab, ConnectSummary} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {ACCENT, INK, MUTED, money} from './ui'

type ConnectStatus = 'NONE' | 'PENDING' | 'VERIFIED'

type Stat = {
  key: string
  label: string
  value: string
  hint: string
  icon: string
  to: string
  accent: string
}

/**
 * Housekeeping overview — clickable stat cards. Each opens its own list page
 * (queue, earnings, payments, door codes).
 */
const CleanerDashboard: FC = () => {
  const navigate = useNavigate()
  const [queue, setQueue] = useState<BedWash[]>([])
  const [tab, setTab] = useState<CleanerTab | null>(null)
  const [payouts, setPayouts] = useState<CleanerPayout[]>([])
  const [codes, setCodes] = useState<DoorCode[]>([])
  const [connect, setConnect] = useState<ConnectStatus | null>(null)

  useEffect(() => {
    Promise.all([
      api.bedWashQueue().catch(() => []),
      api.cleanerTab().catch(() => null),
      api.myCleanerPayouts().catch(() => []),
      api.adminDoorCodes().catch(() => []),
      api.myConnectStatus().catch(() => ({status: 'NONE' as ConnectStatus})),
    ]).then(([q, t, p, c, cn]) => {
      setQueue(q)
      setTab(t)
      setPayouts(p)
      setCodes(c)
      setConnect(cn.status)
    })
  }, [])

  const paidTotal = payouts.reduce((s, p) => s + parseFloat(p.amount || '0'), 0)

  const stats: Stat[] = [
    {key: 'queue', label: 'Bed-wash queue', value: String(queue.length), hint: queue.length === 1 ? 'job to do' : 'jobs to do', icon: '🧺', to: '/housekeeping/queue', accent: '#2b4eff'},
    {key: 'owed', label: 'Owed to you', value: money(tab?.owed ?? '0'), hint: `${tab?.owedCount ?? 0} on your tab`, icon: '💰', to: '/housekeeping/earnings', accent: ACCENT},
    {key: 'paid', label: 'Paid out to you', value: money(paidTotal), hint: `${payouts.length} payment${payouts.length === 1 ? '' : 's'}`, icon: '✅', to: '/housekeeping/payments', accent: '#0aa15a'},
    {key: 'codes', label: 'Door codes', value: String(codes.length), hint: 'entry codes', icon: '🔑', to: '/housekeeping/door-codes', accent: '#b8770a'},
  ]

  return (
    <div>
      {tab?.pendingCount ? (
        <div style={{background: 'rgba(255,168,0,0.12)', border: '1px solid rgba(255,168,0,0.4)', borderRadius: 12, padding: '12px 16px', marginBottom: 18, fontSize: 14, color: '#8a5a00'}}>
          {tab.pendingCount} cleaning visit{tab.pendingCount === 1 ? '' : 's'} awaiting the admin’s approval.
        </div>
      ) : null}

      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 24}}>
        {stats.map((s) => (
          <button
            key={s.key}
            onClick={() => navigate(s.to)}
            className='ccp-lift'
            style={{
              textAlign: 'left', cursor: 'pointer', background: '#fff', border: '1px solid #eef0f4',
              borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', padding: '20px 22px',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <div style={{width: 42, height: 42, borderRadius: 12, background: `${s.accent}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22}}>{s.icon}</div>
              <span style={{color: s.accent, fontSize: 20, fontWeight: 800}}>›</span>
            </div>
            <div>
              <div style={{fontSize: 28, fontWeight: 900, color: INK, lineHeight: 1.1}}>{s.value}</div>
              <div style={{fontSize: 14, fontWeight: 700, color: INK, marginTop: 4}}>{s.label}</div>
              <div style={{fontSize: 12.5, color: MUTED, marginTop: 2}}>{s.hint}</div>
            </div>
          </button>
        ))}
      </div>

      {connect && <PayoutCard initial={connect} />}

      <button
        onClick={() => navigate('/housekeeping/log-visit')}
        className='ccp-lift'
        style={{
          width: '100%', textAlign: 'left', cursor: 'pointer', border: '1px solid #d7ebe0',
          borderRadius: 16, background: 'linear-gradient(135deg, #ffffff, #f1faf4)', padding: '18px 22px',
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
        }}
      >
        <div style={{width: 46, height: 46, borderRadius: 12, background: `${ACCENT}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0}}>🧽</div>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{fontSize: 15.5, fontWeight: 800, color: INK}}>Log a cleaning visit</div>
          <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>Did an apartment cleaning? Add it to your tab.</div>
        </div>
        <span style={{color: ACCENT, fontSize: 22, fontWeight: 800}}>›</span>
      </button>

      <div style={{textAlign: 'center', color: MUTED, fontSize: 13}}>
        Tap a card to see its list.
      </div>
    </div>
  )
}

// ─── Her own Stripe payout onboarding, on her overview ─────────────────────
const PayoutCard: FC<{initial: ConnectStatus}> = ({initial}) => {
  const [status, setStatus] = useState<ConnectStatus>(initial)
  const [busy, setBusy] = useState<'link' | 'refresh' | null>(null)
  const [msg, setMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)
  const [summary, setSummary] = useState<ConnectSummary | null>(null)
  const [opening, setOpening] = useState(false)

  useEffect(() => {
    if (status === 'VERIFIED') api.myConnectSummary().then(setSummary).catch(() => {})
  }, [status])

  const openDashboard = async () => {
    setOpening(true); setMsg(null)
    try {
      const {url} = await api.myConnectLoginLink()
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not open your Stripe dashboard.'})
    } finally {
      setOpening(false)
    }
  }

  const goSetup = async () => {
    setBusy('link'); setMsg(null)
    try {
      const {url} = await api.myConnectLink()
      window.location.href = url // straight into Stripe; returns to /housekeeping
    } catch (e) {
      setBusy(null)
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not open the payout setup. Please try again.'})
    }
  }

  const refresh = async () => {
    setBusy('refresh'); setMsg(null)
    try {
      const {status: s} = await api.myConnectRefresh()
      setStatus(s)
      setMsg(s === 'VERIFIED'
        ? {kind: 'ok', text: 'You’re all set — payouts are ready.'}
        : {kind: 'err', text: 'Not verified yet. You may still need to finish the Stripe steps.'})
    } catch (e) {
      setMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not check your status.'})
    } finally {
      setBusy(null)
    }
  }

  if (status === 'VERIFIED') {
    const bank = summary?.bankName || summary?.bankLast4
    return (
      <div style={{background: 'rgba(3,149,64,0.06)', border: '1px solid rgba(3,149,64,0.25)', borderRadius: 14, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap'}}>
        <div style={{width: 42, height: 42, borderRadius: 12, background: 'rgba(3,149,64,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0}}>🏦</div>
        <div style={{flex: 1, minWidth: 200}}>
          <div style={{fontSize: 14.5, fontWeight: 800, color: '#1f9d5b'}}>Payouts are set up — you’re ready to be paid.</div>
          <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>
            {bank
              ? <>Deposits go to {summary?.bankName ?? 'your bank'} ••••{summary?.bankLast4 ?? '····'}.</>
              : 'Your bank details are securely on file with Stripe.'}
          </div>
          {msg && <div style={{marginTop: 8, fontSize: 12.5, fontWeight: 600, color: msg.kind === 'ok' ? '#1f9d5b' : '#dc2626'}}>{msg.text}</div>}
        </div>
        <button onClick={openDashboard} disabled={opening} style={{...btnCta(opening), background: '#eef1f6', color: INK}}>
          {opening && <Spinner color={INK} />} Manage payout details
        </button>
      </div>
    )
  }

  const isPending = status === 'PENDING'
  return (
    <div className='ccp-lift' style={{border: '1px solid #f0e2c4', borderRadius: 16, background: 'linear-gradient(135deg, #fffdf7, #fdf6e6)', padding: '18px 22px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'}}>
      <div style={{width: 46, height: 46, borderRadius: 12, background: 'rgba(184,119,10,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0}}>🏦</div>
      <div style={{flex: 1, minWidth: 200}}>
        <div style={{fontSize: 15.5, fontWeight: 800, color: INK}}>
          {isPending ? 'Finish setting up your payouts' : 'Set up your payouts'}
        </div>
        <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>
          {isPending
            ? 'You’ve started but haven’t finished. Pick up where you left off so you can be paid.'
            : 'Verify your identity and bank with Stripe so you can be paid directly. It only takes a few minutes.'}
        </div>
        {msg && (
          <div style={{marginTop: 10, fontSize: 12.5, fontWeight: 600, color: msg.kind === 'ok' ? '#1f9d5b' : '#dc2626'}}>{msg.text}</div>
        )}
      </div>
      <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
        <button onClick={goSetup} disabled={busy !== null} style={{...btnCta(busy !== null), background: ACCENT, color: '#fff'}}>
          {busy === 'link' && <Spinner />} {isPending ? 'Continue setup' : 'Set up payouts'}
        </button>
        {isPending && (
          <button onClick={refresh} disabled={busy !== null} style={{...btnCta(busy !== null), background: '#eef1f6', color: INK}}>
            {busy === 'refresh' && <Spinner color={INK} />} I’ve finished
          </button>
        )}
      </div>
    </div>
  )
}

const btnCta = (busy: boolean): React.CSSProperties => ({
  fontWeight: 700, fontSize: 14, border: 'none', borderRadius: 10, padding: '11px 20px',
  cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
  display: 'inline-flex', alignItems: 'center', gap: 8,
})

export {CleanerDashboard}
